import { useEffect, useRef, useState } from "react";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDoorOpen,
  faWifi,
  faBatteryThreeQuarters,
  faSun,
  faTemperatureLow,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import ValveComponent from "./ValveComponent";
import Concrete_Bed from "./3D_irrigation_renders/Concrete_bed2.png.svg";
import Main_line_green from "./3D_irrigation_renders/Main_line_red.svg";
import main_line_red from "./3D_irrigation_renders/Main_line_Red.svg.svg";
import pressuremeter from "./3D_irrigation_renders/Pressure_Metre.svg";
import Watermeter from "./3D_irrigation_renders/Water_Metre.svg";
import antenapole from "./3D_irrigation_renders/Pole_antenna.png";
import ProgressBar from "./ProgressBar";
import ScheduelPopup from "./ScheduelPopup";

const extractSovs = (data: any): boolean[] =>
  Array.from({ length: 14 }, (_, i) => !!data[`sov${i + 1}`]);

type MainPanel = "details" | "mode";
type ModePanel = "manual" | "flow" | "pressuresustaining" | "pressurereducing";

const pillBtn = (active: boolean, disabled: boolean): React.CSSProperties => ({
  padding: "6px 9px", // 👈 18px se 10px kiya
  borderRadius: 999,
  border: "none",
  fontSize: 12, // 👈 12px se 11px kiya
  fontWeight: active ? 700 : 400,
  background: active ? "#2b5e5e" : "transparent",
  color: active ? "#fff" : "#2b5e5e",
  cursor: disabled ? "not-allowed" : "pointer",
  userSelect: "none",
  outline: "none",
  whiteSpace: "nowrap" as const,
  opacity: disabled ? 0.5 : 1,
});

const pillWrap: React.CSSProperties = {
  display: "inline-flex",
  flexWrap: "wrap" as const, // 👈 add karo
  justifyContent: "center",
  background: "#e8f0f0",
  borderRadius: 999,
  padding: 3,
  gap: 4,
  rowGap: 5, // 👈 rows ke beech gap
};

const cmdBtn = (bg: string, disabled: boolean): React.CSSProperties => ({
  padding: "10px 14px",
  fontSize: 12,
  borderRadius: 6,
  border: "none",
  background: disabled ? "#999" : bg,
  color: "#fff",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1,
  transition: "all 0.2s",
  zIndex: 3,
});

// ── Control row ───────────────────────────────────────────────
const ControlRow = ({
  label,
  value,
  onChange,
  onSet,
  onGet,
  showGet,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onSet: () => void;
  onGet: () => void;
  showGet: boolean;
  disabled: boolean;
}) => (
  <div
    className="d-flex flex-column align-items-center"
    style={{ padding: "12px 10px", borderBottom: "1px solid #e8e8e8" }}
  >
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: "#2b5e5e",
        marginBottom: 8,
      }}
    >
      {label}
    </div>
    <div style={{ display: "flex", gap: 6 }}>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Value"
        disabled={disabled}
        style={{
          width: 80,
          padding: "5px 8px",
          fontSize: 12,
          border: "1px solid #ccc",
          borderRadius: 6,
          outline: "none",
          opacity: disabled ? 0.6 : 1,
        }}
      />
      <button
        onClick={onSet}
        disabled={disabled}
        style={cmdBtn("#2b5e5e", disabled)}
      >
        Set
      </button>
      {showGet && (
        <button
          onClick={onGet}
          disabled={disabled}
          style={cmdBtn("#89AC46", disabled)}
        >
          Get
        </button>
      )}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────

const formatSetPoint = (val: number | string | undefined | null): string => {
  if (val === undefined || val === null || val === "") return "";
  const str = String(val);
  return str.includes(".") ? str : `${str}.0`;
};

const ChackComponent = ({
  id,
  onClose,
}: {
  id: number;
  onClose: () => void;
}) => {
  const [device, setDevice] = useState<any>(null);
  const [sovList, setSovList] = useState<boolean[]>([]);

  const [mainPanel, setMainPanel] = useState<MainPanel>("details");
  const [modePanel, setModePanel] = useState<ModePanel>("manual");
  const [view, setView] = useState<"main" | "schedule">("main");
  const flowDirtyRef = useRef(false);
  const pressureDirtyRef = useRef(false);
  const sustainingPressureRef = useRef(false);
  const reducingPressureRef = useRef(false);
  const [flowValue, setFlowValue] = useState("");
  const [pressureValue, setPressureValue] = useState("");
  const [sustainingPressureValue, setSustainingPressureValue] = useState("");
  const [reducingPressureValue, setReducingPressureValue] = useState("");
  // ── Command loading state ─────────────────────────────────────
  const [cmdLoading, setCmdLoading] = useState(false);
  const [progNow, setProgNow] = useState(0);
  const progInterval = useRef<NodeJS.Timeout | null>(null);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [sovtLoading, setSovtLoading] = useState(false); // 👈 naya, independent
  const [sovtProgress, setSovtProgress] = useState(0);
  const sovtInterval = useRef<NodeJS.Timeout | null>(null);

  const [dtimMode, setDtimMode] = useState<"command" | "live" | null>(null);
  const [flowDirty, setFlowDirty] = useState(false);
  const [pressureDirty, setPressureDirty] = useState(false);
  const gradientStyle: React.CSSProperties = {
    background: "#2b5e5e",
    padding: "6px 0px",
  };

  // ── Progress bar ──────────────────────────────────────────────
  const startProgress = (action: string) => {
    if (progInterval.current) clearInterval(progInterval.current);
    setProgNow(0);
    setCmdLoading(true);
    setActiveAction(action);
    const increment = 100 / (40000 / 250); // 👈 25000 → 40000
    let current = 0;
    progInterval.current = setInterval(() => {
      current += increment;
      if (current >= 100) {
        current = 100;
        clearInterval(progInterval.current!);
        progInterval.current = null;
      }
      setProgNow(current);
    }, 250);
  };
  const stopProgress = () => {
    if (progInterval.current) {
      clearInterval(progInterval.current);
      progInterval.current = null;
    }
    setProgNow(0);
    setCmdLoading(false);
    setActiveAction(null);
  };

  // ── Fetch device ──────────────────────────────────────────────
  const fetchDevice = async (syncModePanel = false) => {
    try {
      const res = await ApiService.axiosApi(
        `DemoDevice/GetDemoDeviceByID/${id}`,
        { method: "GET" },
      );
      if (res.success) {
        setDevice(res.data);
        setSovList(extractSovs(res.data));

        if (!flowDirtyRef.current)
          setFlowValue(formatSetPoint(res.data.flowSetPoint));
        if (!pressureDirtyRef.current)
          setPressureValue(formatSetPoint(res.data.pressureSetPoint));
        if (!sustainingPressureRef.current)
          setSustainingPressureValue(
            formatSetPoint(res.data.sustainingPressure),
          );
        if (!reducingPressureRef.current)
          setReducingPressureValue(formatSetPoint(res.data.reducingPressure));

        if (syncModePanel) {
          const mode = res.data.mode;
          if (mode === 1) setModePanel("manual");
          else if (mode === 2) setModePanel("flow");
          else if (mode === 3) setModePanel("pressuresustaining");
          else if (mode === 4) setModePanel("pressurereducing");
        }
      } else {
        toast.error("Failed to load device");
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchDevice(true); // 👈 initial load pe sync karo (fresh open)

    const interval = setInterval(() => {
      fetchDevice(false); // 👈 background poll — user ka tab mat chhedo
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [id]);

  useEffect(() => {
    return () => {
      if (sovtInterval.current) clearInterval(sovtInterval.current);
    };
  }, []);

  if (!device) return <div className="p-4 text-center">Loading</div>;

  const activeSOVCount = sovList.filter(Boolean).length;
  const numberOfValves = device.numberOfValve ?? 14;
  const initialMode: ModePanel =
    device.mode === 1
      ? "manual"
      : device.mode === 2
        ? "flow"
        : device.mode === 3
          ? "pressuresustaining"
          : "pressurereducing";
  const showGet = modePanel !== "manual" && modePanel === initialMode;
  const sovDisabled =
    cmdLoading || device.mode === 2 || device.mode === 3 || device.mode === 4;
  // ── Commands ──────────────────────────────────────────────────
  const handleSet = async (
    type: "manual" | "flow" | "pressureSustaining" | "pressureReducing",
  ) => {
    const mode =
      type === "manual"
        ? 1
        : type === "flow"
          ? 2
          : type === "pressureSustaining"
            ? 3
            : 4;
    const value =
      type === "flow"
        ? flowValue
        : type === "pressureSustaining"
          ? sustainingPressureValue
          : reducingPressureValue;

    if (type !== "manual" && !value) {
      toast.error("Please enter value");
      return;
    }

    startProgress("set-mode");
    try {
      const res = await ApiService.axiosApi("Command/movm", {
        method: "POST",
        data: {
          deviceId: device.id,
          mode,
          value: type === "manual" ? 0 : parseFloat(value).toFixed(1),
        },
      });
      // handleSet success block
      if (res.success) {
        toast.success(`✅ ${type} set`);
        if (type === "flow") flowDirtyRef.current = false;
        if (type === "pressureSustaining")
          sustainingPressureRef.current = false;
        if (type === "pressureReducing") reducingPressureRef.current = false;
        setTimeout(() => fetchDevice(true), 200);
      } else toast.error("Failed");
    } catch (e) {
      console.error(e);
    } finally {
      stopProgress();
    }
  };

  const handleGet = async () => {
    startProgress("get-movm");
    try {
      const res = await ApiService.axiosApi(`Command/get/movm/${device.id}`, {
        method: "GET",
      });
      if (res.success) {
        const parts = (res.data.message ?? "").split(" ");
        const mode = parseInt(parts[1]);
        const val = parts[2] ?? "0";
        if (mode === 1) setModePanel("manual");
        // handleGet
        if (mode === 2) {
          setModePanel("flow");
          setFlowValue(val);
          flowDirtyRef.current = false;
        } else if (mode === 3) {
          setModePanel("pressuresustaining");

          setSustainingPressureValue(val);
          sustainingPressureRef.current = false;
        } else if (mode === 4) {
          setModePanel("pressurereducing");
          setReducingPressureValue(val);
          reducingPressureRef.current = false;
        }

        toast.success(
          `✅Set To Mode: ${mode === 1 ? "Manual" : mode === 2 ? "Flow" : mode === 3 ? "Sustaining Pressure" : "Reducing Pressure"} With ${mode !== 1 ? `, Value: ${val}` : ""}`,
        );
      } else toast.error("Failed");
    } catch (e) {
      console.error(e);
    } finally {
      stopProgress();
    }
  };

  const handleInterrogate = async () => {
    startProgress("interrogate");
    try {
      const res = await ApiService.axiosApi("Command/intg", {
        method: "POST",
        data: { command: "INTG", deviceId: device.id },
      });
      if (res.success) {
        setTimeout(() => fetchDevice(true), 400);
        toast.success("✅ Interrogate successfully");
      } else toast.error("Failed");
    } catch (e) {
      console.error(e);
    } finally {
      stopProgress();
    }
  };

  // handleScheduleAutoManual function add karo
  const handleScheduleAutoManual = async () => {
    startProgress("auto-manual");
    try {
      const res = await ApiService.axiosApi(
        "Command/UpdateScheduleAutoManual",
        {
          method: "POST",
          data: {
            deviceId: device.id,
            autoManual: !device.scheduleAuto, // toggle — false→true, true→false
          },
        },
      );
      if (res.success) {
        toast.success(
          `✅ Schedule ${device.scheduleAuto ? "Auto" : "Manual"} set`,
        );
        setTimeout(() => {
          fetchDevice(false);
        }, 600);
        // refresh device data
      } else {
        toast.error("Failed");
      }
    } catch (e) {
      console.error(e);
    } finally {
      stopProgress();
    }
  };

  const handleSetDtim = async (intervals: string, mode: "command" | "live") => {
    startProgress("setdtim");
    try {
      const res = await ApiService.axiosApi("Command/SetDtim", {
        method: "POST",
        data: {
          deviceId: device.id,
          command: intervals,
        },
      });
      if (res.success) {
        toast.success(`✅ ${mode === "command" ? "Command" : "Status"} Mode set`);
        setDtimMode(mode);
        setTimeout(() => fetchDevice(false), 600);
      } else {
        toast.error("Failed");
      }
    } catch (e) {
      console.error(e);
    } finally {
      stopProgress();
    }
  };

  const handleSovt = async (on_off: boolean) => {
    if (!device.scheduleAuto) {
      toast.error("Please select roms manual mode");
      return;
    }

    startProgress("sov");
    try {
      const res = await ApiService.axiosApi("Command/setsovt", {
        method: "POST",
        data: {
          deviceId: device.id,
          command: `${on_off ? "SOVT 2 20000" : "SOVT 1 20000"}`,
        },
      });
      if (res.success) {
        toast.success(`✅ Success`);
        setTimeout(() => fetchDevice(false), 600);

        // 👇 20 sec ka determinate progress bar
        setSovtLoading(true);
        setSovtProgress(0);

        const duration = 20000;
        const increment = 100 / (duration / 250);
        let current = 0;

        if (sovtInterval.current) clearInterval(sovtInterval.current);
        sovtInterval.current = setInterval(() => {
          current += increment;
          if (current >= 100) {
            current = 100;
            clearInterval(sovtInterval.current!);
            sovtInterval.current = null;
            setSovtLoading(false);
          }
          setSovtProgress(current);
        }, 250);
      } else {
        toast.error("failed");
      }
    } catch (e) {
      console.error(e);
      toast.error("❌ Error");
    } finally {
      stopProgress();
    }
  };

  // const handleSovToggle = async (sovNumber: number, onOff: boolean) => {
  //   if (!device.scheduleAuto) {
  //     toast.error("Please select roms manual mode");
  //     return;
  //   }

  //   startProgress("sov");
  //   try {
  //     const res = await ApiService.axiosApi("Command/sov/open/close", {
  //       method: "POST",
  //       data: {
  //         deviceId: device.id,
  //         sovNumber,
  //         onOff,
  //       },
  //     });
  //     if (res.success) {
  //       toast.success(`✅ SOV${sovNumber} ${onOff ? "opened" : "closed"}`);
  //       setTimeout(() => fetchDevice(false), 600);
  //     } else {
  //       toast.error("Command failed");
  //     }
  //   } catch (e) {
  //     console.error(e);
  //     toast.error("❌ Error");
  //   } finally {
  //     stopProgress();
  //   }
  // };

  return (
    <div className="modal-content d-xl-flex">
      {/* ── Header ── */}
      <div
        className="modal-header"
        style={{ ...gradientStyle, position: "relative" }}
      >
        {/* Progress bar — header ke bottom edge par */}
        {cmdLoading && (
          <ProgressBar
            totalpage={0}
            pageNumber={0}
            now={progNow}
            mystyle={{
              position: "absolute",
              top: "-23px",
              bottom: 0,
              left: 0,
              height: "4px",
              width: "100%",
              zIndex: 10,
              backgroundColor: "transparent",
              borderRadius: 0,
            }}
          />
        )}

        <div className="col-md-12">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h5 className="modal-title" style={{ color: "white" }}>
                <strong>{device.deviceName}</strong>
                <small className="ms-2" style={{ fontSize: 12, opacity: 0.8 }}>
                  {device.deviceType}
                </small>
              </h5>
            </div>
            <div className="col-md-6 d-flex justify-content-end gap-2 align-items-center px-4">
              <button
                disabled={cmdLoading}
                onClick={handleScheduleAutoManual}
                style={cmdBtn(
                  device.scheduleAuto ? "#F7374F" : "#89AC46",
                  cmdLoading,
                )}
                title={
                  device.scheduleAuto
                    ? "Manual mode — click for Auto"
                    : "Auto mode — click for Manual"
                }
              >
                {device.scheduleAuto ? "Roms Auto" : "Roms Manual"}
              </button>

              {/* Schedule toggle */}
              <button
                disabled={cmdLoading}
                onClick={() =>
                  setView(view === "schedule" ? "main" : "schedule")
                }
                style={cmdBtn("#3a7575", cmdLoading)}
              >
                {view === "schedule" ? "← Back" : "Roms Schedule"}
              </button>

              {/* Interrogate */}
              <button
                disabled={cmdLoading}
                onClick={handleInterrogate}
                style={cmdBtn("#89AC46", cmdLoading)}
              >
                {activeAction == "interrogate" ? "Sending..." : "Interrogate"}
              </button>

              {/* Close */}
              <button
                onClick={onClose}
                style={{
                  background: "none",
                  border: "none",
                  fontWeight: 700,
                  fontSize: 18,
                  cursor: "pointer",
                  color: "white",
                }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="modal-body" style={{ width: "100%", padding: "10px" }}>
        {view === "schedule" ? (
          <ScheduelPopup
            totalValves={numberOfValves}
            ChakId={device.id}
            onBack={() => setView("main")}
          />
        ) : (
          <div
            className="row flex-nowrap"
            style={{ width: "100%", minWidth: 900 }}
          >
            {/* ── Left icon strip ── */}
            <div
              className="col-md-1 "
              style={{ minWidth: 70, borderRight: "0.5px inset grey" }}
            >
              <ul
                className="pop-icons justify-content-center align-items-center"
                style={{ listStyle: "none", padding: 0 }}
              >
                <li
                  className={`d-flex flex-column align-items-center pb-4 ${!device.door1 ? "green" : "red"}`}
                >
                  <FontAwesomeIcon icon={faDoorOpen} size="2x" />
                  <div style={{ fontSize: 11 }}>
                    {device.door1 ? "Open" : "Close"}
                  </div>
                </li>
                <li
                  className={`d-flex flex-column align-items-center pb-4 ${
                    device.networkStrength == "Excellent"
                      ? "green"
                      : device.networkStrength == "Good"
                        ? "green"
                        : device.networkStrength == "Average"
                          ? "network-yellow"
                          : device.networkStrength == "Poor"
                            ? "red"
                            : ""
                  }`}
                >
                  <FontAwesomeIcon icon={faWifi} size="2x" />
                  <div style={{ fontSize: 11 }}>{device.networkStrength}</div>
                </li>
                <li
                  className={`${device.lowBatteryBit ? "red" : "green"} d-flex flex-column align-items-center pb-4`}
                >
                  <FontAwesomeIcon icon={faBatteryThreeQuarters} size="2x" />
                  <div style={{ fontSize: 11 }}>{device.batteryVoltage}V</div>
                </li>
                <li className="green d-flex flex-column align-items-center pb-4">
                  <FontAwesomeIcon icon={faSun} size="2x" />
                  <div style={{ fontSize: 11 }}>{device.solarVoltage}V</div>
                </li>
                <li
                  className={`${device.highTemperatureBit ? "red" : "green"} d-flex flex-column align-items-center`}
                >
                  <FontAwesomeIcon icon={faTemperatureLow} size="2x" />
                  <div style={{ fontSize: 11 }}>{device.temperature}°C</div>
                </li>
              </ul>
            </div>

            {/* ── Centre diagram ── */}
            <div className="col-md-8 p-0" style={{ minWidth: 500 }}>
              <div id="valve-component">
                <img id="antena_pole" src={antenapole} alt="" />
                <img
                  id="Main_line_red"
                  src={activeSOVCount > 0 ? Main_line_green : main_line_red}
                  alt=""
                />
                <img id="Concrete_Bed" src={Concrete_Bed} alt="" />
                <img id="pressure-meter" src={pressuremeter} alt="" />
                <img id="water-meter" src={Watermeter} alt="" />

                {sovList
                  .slice(2)
                  .slice(0, numberOfValves)
                  .map((isOn, i) => (
                    <ValveComponent
                      key={i}
                      index={i + 2} // actual SOV: SOV3, SOV4...
                      displayIndex={i} // display: V1, V2...
                      isOn={isOn}
                      chakId={device.id}
                      romsAuto={false}
                      scheduleAuto={device.scheduleAuto ?? false}
                      onRefresh={fetchDevice}
                      isPressureorFlow={device.mode === 2 || device.mode === 3}
                    />
                  ))}
                </div>

                {/* 👇 Command Mode / Live Mode buttons — bottom-left, outside valve-component */}
                {/* 👇 Command Mode / Live Mode buttons — bottom-left, outside valve-component */}
                {/* 👇 Command Mode / Status Mode buttons — bottom-left, outside valve-component */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 10,
                    left: 10,
                    display: "flex",
                    gap: 8,
                    zIndex: 5,
                    backgroundColor: "rgba(43,94,94,0.1)",
                    borderRadius: 6,
                    padding: "8px 10px",
                  }}
                >
                  <button
                    disabled={cmdLoading}
                    onClick={() => handleSetDtim("DTIM 300", "command")}
                    style={cmdBtn(dtimMode === "command" ? "#2b5e5e" : "#89AC46", cmdLoading)}
                  >
                    {activeAction === "setdtim" ? "Sending..." : "Command Mode"}
                  </button>
                  <button
                    disabled={cmdLoading}
                    onClick={() => handleSetDtim("DTIM 10", "live")}
                    style={cmdBtn(dtimMode === "live" ? "#2b5e5e" : "#3a7575", cmdLoading)}
                  >
                    {activeAction === "setdtim" ? "Sending..." : "Status Mode"}
                  </button>
                </div>
            </div>

            {/* ── Right panel ── */}
            <div className="col-md-3 p-0" style={{ minWidth: 280 }}>
              <div
                className="card z-1"
                style={{
                  width: "100%",
                  border: mainPanel === "mode" ? "1px solid #2b5e5e" : "none",
                }}
              >
                {/* Level 1 pill */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    backgroundColor: "rgba(43,94,94,0.1)",
                    borderRadius: 5,
                    paddingTop: 5,
                    paddingBottom: 5,
                  }}
                >
                  <div style={pillWrap}>
                    <button
                      style={pillBtn(mainPanel === "details", false)}
                      onClick={() => setMainPanel("details")}
                    >
                      Details
                    </button>
                    <button
                      style={pillBtn(mainPanel === "mode", false)}
                      onClick={() => setMainPanel("mode")}
                    >
                      Pfcv Mode
                    </button>
                  </div>
                </div>

                {/* Level 2 pill */}
                {mainPanel === "mode" && (
                  <div
                    style={{
                      padding: "4px 5px 0",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <div style={pillWrap}>
                      <button
                        style={pillBtn(modePanel === "manual", cmdLoading)}
                        disabled={cmdLoading}
                        onClick={() => setModePanel("manual")}
                      >
                        Manual
                      </button>
                      <button
                        style={pillBtn(modePanel === "flow", cmdLoading)}
                        disabled={cmdLoading}
                        onClick={() => setModePanel("flow")}
                      >
                        Flow
                      </button>
                      <button
                        style={pillBtn(
                          modePanel === "pressuresustaining",
                          cmdLoading,
                        )}
                        disabled={cmdLoading}
                        onClick={() => setModePanel("pressuresustaining")}
                      >
                        Sustaining
                      </button>

                      <button
                        style={pillBtn(
                          modePanel === "pressurereducing",
                          cmdLoading,
                        )}
                        disabled={cmdLoading}
                        onClick={() => setModePanel("pressurereducing")}
                      >
                        Reducing
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 8 }}>
                  {/* Details */}
                  {mainPanel === "details" && (
                    <>
                      <div
                        className="card-header px-3 py-2 d-flex justify-content-between"
                        style={gradientStyle}
                      >
                        <h6
                          className="card-title mb-0"
                          style={{ color: "white" }}
                        >
                          Details
                        </h6>
                        <div style={{ fontSize: 11, color: "white" }}>
                          {new Date(device.serverDateTime).toLocaleString(
                            "en-IN",
                          )}
                        </div>
                      </div>
                      <div className="card-body p-0">
                        <table
                          className="table table-hover mb-0"
                          style={{ fontSize: 13 }}
                        >
                          <tbody>
                            <tr
                              style={{ backgroundColor: "rgba(43,94,94,0.1)" }}
                            >
                              <td>Pressure</td>
                              <td>{device.pressureTransmitter1} (bar)</td>
                            </tr>

                            <tr>
                              <td>Today Volume</td>
                              <td>{device.waterMeterValue1} (ltr)</td>
                            </tr>

                            <tr
                              style={{ backgroundColor: "rgba(43,94,94,0.1)" }}
                            >
                              <td>Flow Rate</td>
                              <td>{device.flowRate} (lps)</td>
                            </tr>

                            <tr>
                              <td>Schedule Mode</td>
                              <td>{device.scheduleAuto ? "Manual" : "Auto"}</td>
                            </tr>

                            <tr
                              style={{ backgroundColor: "rgba(43,94,94,0.1)" }}
                            >
                              <td>Pfcv Mode</td>
                              <td
                                style={{
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                              >
                                {device.mode == 1
                                  ? "Manual"
                                  : device.mode == 2
                                    ? "Flow Rate"
                                    : device.mode == 3
                                      ? "Sustaining Pres.."
                                      : device.mode == 4
                                        ? "Reducing Pres.."
                                        : "Not Set"}
                              </td>
                            </tr>

                            <tr>
                              <td>Flow Set Point</td>
                              <td>
                                {formatSetPoint(device.flowSetPoint)} (ltr)
                              </td>
                            </tr>

                            <tr
                              style={{ backgroundColor: "rgba(43,94,94,0.1)" }}
                            >
                              <td>Sustaining Pressure</td>
                              <td>
                                {formatSetPoint(device.sustainingPressure)}{" "}
                                (bar)
                              </td>
                            </tr>

                            <tr>
                              <td>Reducing Pressure</td>
                              <td>
                                {formatSetPoint(device.reducingPressure)} (bar)
                              </td>
                            </tr>

                            <tr
                              style={{ backgroundColor: "rgba(43,94,94,0.1)" }}
                            >
                              <td>Process</td>
                              <td>{device.flowStatus}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                  {/* Manual */}
                  {mainPanel === "mode" && modePanel === "manual" && (
                    <div style={{ padding: "12px 10px" }}>
                      <div className="d-flex flex-column align-items-center justify-content-center">
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#2b5e5e",
                            marginBottom: 8,
                          }}
                        >
                          Manual Mode
                        </div>
                        <button
                          disabled={cmdLoading}
                          onClick={() => handleSet("manual")}
                          style={{
                            ...cmdBtn("#2b5e5e", cmdLoading),
                            marginBottom: 14,
                          }}
                        >
                          {activeAction == "set-mode"
                            ? "Sending..."
                            : "Set Manual"}
                        </button>
                      </div>

                      {[1].map((sovNumber) => (
                        <div
                          key={sovNumber}
                          className="d-flex flex-column align-items-center justify-content-center"
                          style={{
                            marginBottom: 10,
                            gap: 8,
                            backgroundColor: "rgba(43,94,94,0.1)",
                            borderRadius: 6,
                            padding: "8px 10px",
                            width: "100%",
                          }}
                        >
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              disabled={sovDisabled || sovtLoading}
                              onClick={() => handleSovt(true)}
                              style={cmdBtn(
                                "#89AC46",
                                sovDisabled || sovtLoading,
                              )}
                            >
                              Open
                            </button>
                            <button
                              disabled={sovDisabled || sovtLoading}
                              onClick={() => handleSovt(false)}
                              style={cmdBtn(
                                "#F7374F",
                                sovDisabled || sovtLoading,
                              )}
                            >
                              Close
                            </button>
                          </div>

                          {/* 👇 20 sec ka progress bar */}
                          {sovtLoading && (
                            <div
                              style={{
                                width: "100%",
                                height: 6,
                                background: "#d9e6e6",
                                borderRadius: 999,
                                overflow: "hidden",
                                marginTop: 4,
                              }}
                            >
                              <div
                                style={{
                                  width: `${sovtProgress}%`,
                                  height: "100%",
                                  background: "#2b5e5e",
                                  borderRadius: 999,
                                  transition: "width 0.25s linear",
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Flow */}
                  {mainPanel === "mode" && modePanel === "flow" && (
                    <ControlRow
                      label="Flow Rate (ltr)"
                      value={flowValue}
                      onChange={(v) => {
                        setFlowValue(v);
                        flowDirtyRef.current = true; // 👈 hamesha dirty, empty ho ya na ho
                      }}
                      onSet={() => handleSet("flow")}
                      onGet={handleGet}
                      showGet={showGet}
                      disabled={cmdLoading}
                    />
                  )}
                  {mainPanel === "mode" &&
                    modePanel === "pressuresustaining" && (
                      <ControlRow
                        label="Sustaining Pressure(bar)"
                        value={sustainingPressureValue}
                        onChange={(v) => {
                          setSustainingPressureValue(v);
                          sustainingPressureRef.current = true; // 👈 hamesha dirty
                        }}
                        onSet={() => handleSet("pressureSustaining")}
                        onGet={handleGet}
                        showGet={showGet}
                        disabled={cmdLoading}
                      />
                    )}

                  {mainPanel === "mode" && modePanel === "pressurereducing" && (
                    <ControlRow
                      label="Reducing Pressure (bar)"
                      value={reducingPressureValue}
                      onChange={(v) => {
                        setReducingPressureValue(v);
                        reducingPressureRef.current = true; // 👈 hamesha dirty
                      }}
                      onSet={() => handleSet("pressureReducing")}
                      onGet={handleGet}
                      showGet={showGet}
                      disabled={cmdLoading}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChackComponent;
